import { useState } from 'react'
import { 
  FileText, 
  Upload, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Database,
  Brain,
  Workflow,
  Zap,
  BarChart3,
  FileCheck,
  Scan,
  Search,
  Filter,
  ArrowRight,
  Download,
  Settings,
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  Target,
  Cpu,
  HardDrive,
  Globe,
  ArrowLeft,
  LogOut
} from 'lucide-react'
import InfoTooltip from './common/InfoTooltip'

export default function DocumentAutomationDemo({ onLogout }) {
  const [selectedDocument, setSelectedDocument] = useState(0)
  const [processingStage, setProcessingStage] = useState('completed')
  const [showDocumentDetail, setShowDocumentDetail] = useState(false)
  const [showProcessingDetail, setShowProcessingDetail] = useState(false)
  const [simulateProcessing, setSimulateProcessing] = useState(false)

  // Metricas principales del sistema
  const metrics = [
    {
      title: "Precisión OCR",
      value: "98.7",
      suffix: "%",
      change: "+2.1%",
      trend: "up",
      icon: Scan,
      color: "blue",
             tooltip: {
         title: "Precisión Ultra-Alta que Elimina Errores Costosos",
         description: "98.7% de precisión significa que prácticamente no hay errores de transcripción. Compare esto con 70-80% de precisión manual que cuesta miles en correcciones.",
         aiMethod: "💰 Beneficio: Elimine 95% de errores humanos en captura de datos. Ahorro promedio de $25,000 anuales solo en correcciones y reprocesamiento.",
         dataSource: "📈 ROI: Reducción del 90% en tiempo de validación manual. Sus empleados se enfocan en análisis de valor agregado, no en captura tediosa.",
         updateFrequency: "🚀 Implementación: Resultados inmediatos desde el primer documento procesado"
       }
    },
    {
      title: "Tiempo Procesamiento",
      value: "12",
      suffix: " seg",
      change: "-45%",
      trend: "up",
      icon: Clock,
      color: "green",
             tooltip: {
         title: "Velocidad que Transforma su Operación",
         description: "12 segundos vs 15-30 minutos manuales. Procese 150x más documentos en el mismo tiempo. Su equipo puede enfocarse en decisiones estratégicas.",
         aiMethod: "💰 Beneficio: Libere 80% del tiempo de su equipo para actividades de mayor valor. Aumente productividad sin contratar más personal.",
         dataSource: "📈 ROI: Capacidad de procesar 500% más volumen sin aumentar costos operativos. Escalabilidad inmediata para crecimiento.",
         updateFrequency: "🚀 Implementación: Velocidad máxima desde el primer día, optimización automática continua"
       }
    },
    {
      title: "Validación Automática",
      value: "94.2",
      suffix: "%",
      change: "+7.8%",
      trend: "up",
      icon: CheckCircle,
      color: "purple",
             tooltip: {
         title: "Automatización que Elimina Cuellos de Botella",
         description: "94.2% de documentos procesados sin intervención humana. Elimine las colas de aprobación que retrasan su operación y frustran clientes.",
         aiMethod: "💰 Beneficio: Reduzca tiempos de aprobación de días a minutos. Mejore flujo de efectivo con procesamiento instantáneo de facturas.",
         dataSource: "📈 ROI: Aumento del 300% en velocidad de procesamiento. Reducción del 70% en quejas por demoras administrativas.",
         updateFrequency: "🚀 Implementación: Automatización completa en 1 semana, optimización inteligente 24/7"
       }
    },
    {
      title: "Documentos/Día",
      value: "2,847",
      suffix: "",
      change: "+156%",
      trend: "up",
      icon: FileText,
      color: "orange",
             tooltip: {
         title: "Capacidad Industrial sin Límites",
         description: "2,847 documentos diarios procesados automáticamente. Su competencia procesa 200-300 manualmente. Esta ventaja le permite capturar más clientes.",
         aiMethod: "💰 Beneficio: Capacidad para manejar 10x más volumen sin contratar personal. Acepte más proyectos y clientes sin aumentar costos.",
         dataSource: "📈 ROI: Crecimiento de ingresos del 150% manteniendo los mismos gastos operativos. Escalabilidad ilimitada.",
         updateFrequency: "🚀 Implementación: Capacidad máxima disponible inmediatamente, crecimiento automático según demanda"
       }
    }
  ]

  // Estados del pipeline de procesamiento
  const processingStages = [
    { id: 'upload', name: 'Carga', icon: Upload, status: 'completed' },
    { id: 'ocr', name: 'OCR', icon: Scan, status: 'completed' },
    { id: 'extraction', name: 'Extracción', icon: Search, status: 'completed' },
    { id: 'validation', name: 'Validación', icon: CheckCircle, status: 'completed' },
    { id: 'approval', name: 'Aprobación', icon: FileCheck, status: 'pending' }
  ]

  // Documentos de ejemplo
  const documents = [
         {
       id: 1,
       type: 'Factura',
       filename: 'FACT-A-2024-001.pdf',
       status: 'validated',
       confidence: 0.987,
       extractedData: {
         proveedor: 'Tecnología Integral SA de CV',
         rfc: 'TIN920101ABC',
         folio: 'A-2024-001',
         fecha: '2024-01-15',
         subtotal: '$12,500.00',
         iva: '$2,000.00',
         total: '$14,500.00'
       },
       validationResults: {
         rfcValid: true,
         mathValid: true,
         formatValid: true,
         duplicateCheck: true
       },
       processingTime: '8.4s',
       ocrSteps: [
         { step: 'Preprocesamiento', status: 'completed', time: '1.2s', confidence: 0.98 },
         { step: 'Reconocimiento OCR', status: 'completed', time: '3.1s', confidence: 0.99 },
         { step: 'Extracción de datos', status: 'completed', time: '2.8s', confidence: 0.97 },
         { step: 'Validación cruzada', status: 'completed', time: '1.3s', confidence: 0.98 }
       ],
       businessImpact: {
         manualTime: '25 min',
         timeSaved: '16.6 min',
         costSaving: '$8.50',
         accuracyImprovement: '+15%'
       }
     },
    {
      id: 2,
      type: 'Recibo',
      filename: 'REC-2024-0234.jpg',
      status: 'requires_review',
      confidence: 0.782,
      extractedData: {
        concepto: 'Servicio de consultoría',
        fecha: '2024-01-14',
        monto: '$5,250.00',
        folio: '0234'
      },
      validationResults: {
        formatValid: true,
        mathValid: false,
        duplicateCheck: true
      },
      processingTime: '15.2s'
    },
    {
      id: 3,
      type: 'Contrato',
      filename: 'CONT-SERVICIOS-2024.pdf',
      status: 'processing',
      confidence: 0.923,
      extractedData: {
        partes: 'Empresa XYZ & Consultores ABC',
        vigencia: '2024-01-01 a 2024-12-31',
        monto: '$150,000.00',
        clausulas: 15
      },
      validationResults: {
        structureValid: true,
        legalTerms: true,
        datesValid: true
      },
      processingTime: '23.1s'
    }
  ]

  const getStatusColor = (status) => {
    switch (status) {
      case 'validated': return 'text-green-600 bg-green-100'
      case 'requires_review': return 'text-yellow-600 bg-yellow-100'
      case 'processing': return 'text-blue-600 bg-blue-100'
      case 'error': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'validated': return CheckCircle
      case 'requires_review': return AlertTriangle
      case 'processing': return Clock
      case 'error': return XCircle
      default: return FileText
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => window.history.back()}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                <Scan className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Automatización de Documentos</h1>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-8">
        {/* Title Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Automatización de Procesos de Documentos
          </h1>
          <p className="text-gray-600">
            OCR inteligente, extracción de datos con IA y workflows de validación automatizados
          </p>
        </div>

        {/* Metricas principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => {
            const IconComponent = metric.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{metric.title}</p>
                      <InfoTooltip 
                        title={metric.tooltip.title}
                        description={metric.tooltip.description}
                        aiMethod={metric.tooltip.aiMethod}
                        dataSource={metric.tooltip.dataSource}
                        updateFrequency={metric.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {metric.value}{metric.suffix}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {metric.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">vs mes anterior</span>
                    </div>
                  </div>
                  <div className={`p-3 bg-${metric.color}-500 rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Pipeline de procesamiento */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center space-x-2 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Pipeline de Procesamiento</h3>
              <InfoTooltip
                title="Pipeline Automatizado de Documentos"
                description="Flujo de trabajo que procesa documentos desde la carga hasta la aprobación final con validaciones automáticas."
                aiMethod="Orquestación con Apache Airflow, microservicios con FastAPI, colas de procesamiento con Celery y Redis."
                dataSource="Documentos cargados, APIs de validación externa, bases de datos de referencia, logs de auditoria."
                updateFrequency="Procesamiento continuo con monitoreo en tiempo real"
                iconColor="text-gray-400"
              />
            </div>
            <div className="space-y-4">
              {processingStages.map((stage, index) => {
                const IconComponent = stage.icon
                const isCompleted = stage.status === 'completed'
                const isPending = stage.status === 'pending'
                
                return (
                  <div key={stage.id} className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      isCompleted ? 'bg-green-500' : 
                      isPending ? 'bg-yellow-500' : 'bg-gray-300'
                    }`}>
                      <IconComponent className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${
                        isCompleted ? 'text-green-900' : 
                        isPending ? 'text-yellow-900' : 'text-gray-600'
                      }`}>
                        {stage.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {isCompleted ? 'Completado' : 
                         isPending ? 'En proceso' : 'Pendiente'}
                      </p>
                    </div>
                    {index < processingStages.length - 1 && (
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                )
              })}
            </div>

            {/* Zona de carga */}
            <div className="mt-6 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">Arrastra documentos aquí</p>
              <button className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600">
                Seleccionar archivos
              </button>
            </div>
          </div>

          {/* Lista de documentos */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center space-x-2 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Documentos Procesados</h3>
              <InfoTooltip
                title="Monitor de Documentos en Tiempo Real"
                description="Vista de todos los documentos en el sistema con su estado de procesamiento y datos extraídos."
                aiMethod="Sistema de seguimiento distribuido, webhooks para actualizaciones de estado, métricas de confianza de IA."
                dataSource="Base de datos de documentos, logs de procesamiento, resultados de validación, feedback de usuarios."
                updateFrequency="Actualización en tiempo real con notificaciones push"
                iconColor="text-gray-400"
              />
            </div>
            <div className="space-y-4">
              {documents.map((doc, index) => {
                const StatusIcon = getStatusIcon(doc.status)
                return (
                  <div 
                    key={doc.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedDocument === index 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedDocument(index)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <FileText className="w-5 h-5 text-gray-600" />
                          <h4 className="font-medium text-gray-900">{doc.filename}</h4>
                          <span className="text-sm text-gray-500">({doc.type})</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                            {doc.status}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                          {Object.entries(doc.extractedData).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium capitalize">{key}:</span> {value}
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>Confianza: {(doc.confidence * 100).toFixed(1)}%</span>
                          <span>Tiempo: {doc.processingTime}</span>
                          <span>Validaciones: {Object.values(doc.validationResults).filter(Boolean).length}/{Object.keys(doc.validationResults).length}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <StatusIcon className="w-5 h-5 text-blue-500" />
                                               <div className="flex space-x-1">
                         <button 
                           onClick={() => {
                             setSelectedDocument(index)
                             setShowDocumentDetail(true)
                           }}
                           className="p-1 hover:bg-gray-100 rounded"
                         >
                           <Eye className="w-4 h-4 text-gray-600" />
                         </button>
                         <button className="p-1 hover:bg-gray-100 rounded">
                           <Download className="w-4 h-4 text-gray-600" />
                         </button>
                         <button
                           onClick={() => {
                             setSelectedDocument(index)
                             setShowProcessingDetail(true)
                           }}
                           className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 ml-2"
                         >
                           Ver proceso
                         </button>
                       </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

                 {/* Sección de beneficios comerciales */}
         <div className="mt-8 bg-white rounded-xl shadow-sm border p-6">
           <div className="flex items-center space-x-2 mb-6">
             <h3 className="text-2xl font-semibold text-gray-900">¿Por qué su Empresa Necesita Automatización de Documentos?</h3>
             <Brain className="w-6 h-6 text-purple-600" />
           </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                         {/* Ahorro de Costos Inmediato */}
             <div>
               <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                 <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                 Ahorro de Costos Inmediato
               </h4>
               <div className="space-y-4 text-sm text-gray-700">
                 <div className="p-4 bg-green-50 rounded-lg">
                   <h5 className="font-semibold text-green-900 mb-2">💰 Elimine 80% de Captura Manual</h5>
                   <p>Termine con empleados dedicando horas a transcribir facturas y documentos. El sistema procesa en segundos lo que toma horas manualmente. Redirija ese talento a actividades estratégicas que generen ingresos.</p>
                 </div>
                 <div className="p-4 bg-blue-50 rounded-lg">
                   <h5 className="font-semibold text-blue-900 mb-2">📈 Acelere su Flujo de Efectivo</h5>
                   <p>Procese facturas de proveedores y pagos en minutos, no días. Mejore sus relaciones comerciales con respuestas ultrarrápidas. Aproveche descuentos por pronto pago que antes perdía.</p>
                 </div>
                 <div className="p-4 bg-purple-50 rounded-lg">
                   <h5 className="font-semibold text-purple-900 mb-2">🚀 Escale sin Contratar Personal</h5>
                   <p>Maneje 10x más documentos con el mismo equipo. Perfecto para empresas en crecimiento que no quieren aumentar costos fijos. Capacidad ilimitada de procesamiento.</p>
                 </div>
               </div>
             </div>

                         {/* Ventaja Competitiva y Crecimiento */}
             <div>
               <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                 <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                 Ventaja Competitiva y Crecimiento
               </h4>
               <div className="space-y-4 text-sm text-gray-700">
                 <div className="p-4 bg-yellow-50 rounded-lg">
                   <h5 className="font-semibold text-yellow-900 mb-2">🏆 Supere a su Competencia</h5>
                   <p>Mientras otros procesan manualmente, usted responde en minutos. Capture clientes que exigen rapidez y precisión. Conviértase en el proveedor preferido por su eficiencia operativa.</p>
                 </div>
                 <div className="p-4 bg-red-50 rounded-lg">
                   <h5 className="font-semibold text-red-900 mb-2">⚡ Implementación Express</h5>
                   <p>Sistema funcionando en 1 semana. Sin interrupciones de su operación actual. Capacitación mínima requerida. Vea resultados desde el primer documento procesado.</p>
                 </div>
                 <div className="p-4 bg-indigo-50 rounded-lg">
                   <h5 className="font-semibold text-indigo-900 mb-2">📊 ROI Comprobado</h5>
                   <p>Recupere su inversión en 3-4 meses. Casos documentados muestran ahorros de $40,000-150,000 anuales. Métricas transparentes y reportes automáticos de impacto.</p>
                 </div>
               </div>
             </div>
          </div>

                     {/* Metricas de impacto comercial */}
           <div className="mt-8 p-6 bg-gray-50 rounded-lg">
             <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
               <BarChart3 className="w-5 h-5 text-indigo-600 mr-2" />
               Impacto Real en su Negocio
             </h4>
             <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-sm">
               <div className="text-center">
                 <div className="text-2xl font-bold text-green-600 mb-1">$95,000</div>
                 <div className="text-gray-600">Ahorro anual promedio en costos operativos</div>
               </div>
               <div className="text-center">
                 <div className="text-2xl font-bold text-blue-600 mb-1">85%</div>
                 <div className="text-gray-600">Reducción en tiempo de procesamiento</div>
               </div>
               <div className="text-center">
                 <div className="text-2xl font-bold text-purple-600 mb-1">+300%</div>
                 <div className="text-gray-600">Aumento en capacidad de procesamiento</div>
               </div>
               <div className="text-center">
                 <div className="text-2xl font-bold text-orange-600 mb-1">3.5 meses</div>
                 <div className="text-gray-600">Tiempo promedio de recuperación de inversión</div>
               </div>
             </div>
           </div>
        </div>

        {/* Integración con N8N */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center space-x-2 mb-6">
            <h3 className="text-2xl font-semibold text-gray-900">Integración con N8N y Workflows</h3>
            <Workflow className="w-6 h-6 text-green-600" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <Zap className="w-4 h-4 text-yellow-600 mr-2" />
                Triggers Automáticos
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Webhook al recibir documento por email</li>
                <li>• Monitor de carpeta para archivos nuevos</li>
                <li>• API trigger desde sistemas externos</li>
                <li>• Scheduler para procesamiento batch</li>
              </ul>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <Database className="w-4 h-4 text-blue-600 mr-2" />
                Integraciones
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• ERP (SAP, Oracle, Odoo)</li>
                <li>• Sistemas contables</li>
                <li>• Plataformas de e-commerce</li>
                <li>• APIs bancarias y financieras</li>
              </ul>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <Users className="w-4 h-4 text-purple-600 mr-2" />
                Notificaciones
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Slack/Teams para alertas</li>
                <li>• Email con resumen de procesamiento</li>
                <li>• SMS para errores críticos</li>
                <li>• Dashboard en tiempo real</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Stack tecnológico */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center space-x-2 mb-6">
            <h3 className="text-2xl font-semibold text-gray-900">Stack Tecnológico Completo</h3>
            <HardDrive className="w-6 h-6 text-gray-600" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">IA y ML</div>
              <div className="text-sm text-gray-600">
                Tesseract + PaddleOCR<br/>
                TrOCR (Transformers)<br/>
                spaCy + BERT<br/>
                XGBoost + scikit-learn
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">Backend</div>
              <div className="text-sm text-gray-600">
                Python + FastAPI<br/>
                Celery + Redis<br/>
                Apache Airflow<br/>
                Docker + Kubernetes
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">Almacenamiento</div>
              <div className="text-sm text-gray-600">
                PostgreSQL<br/>
                MinIO (S3-compatible)<br/>
                Elasticsearch<br/>
                Redis (cache)
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">Integración</div>
              <div className="text-sm text-gray-600">
                N8N (workflows)<br/>
                REST + GraphQL APIs<br/>
                RabbitMQ<br/>
                Prometheus + Grafana
              </div>
            </div>
          </div>
        </div>

        {/* Modal de detalle de documento */}
        {showDocumentDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
              {/* Header del modal */}
              <div className="bg-orange-600 text-white p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-semibold">Detalle del Documento</h3>
                    <p className="text-orange-100">{documents[selectedDocument]?.filename}</p>
                  </div>
                  <button
                    onClick={() => setShowDocumentDetail(false)}
                    className="text-white hover:bg-orange-700 rounded-lg p-2"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 h-96 overflow-hidden">
                {/* Vista del documento */}
                <div className="lg:col-span-2 p-6 border-r">
                  <h4 className="font-semibold mb-4">Vista del Documento</h4>
                  <div className="bg-gray-100 rounded-lg h-full flex items-center justify-center">
                    <div className="text-center">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Preview del documento</p>
                      <p className="text-sm text-gray-500">{documents[selectedDocument]?.filename}</p>
                    </div>
                  </div>
                </div>

                {/* Datos extraídos */}
                <div className="p-6 bg-gray-50 overflow-y-auto">
                  <h4 className="font-semibold mb-4">Datos Extraídos</h4>
                  {documents[selectedDocument]?.extractedData && (
                    <div className="space-y-4 text-sm">
                      {Object.entries(documents[selectedDocument].extractedData).map(([key, value]) => (
                        <div key={key} className="p-3 bg-white rounded border">
                          <label className="font-medium text-gray-700 capitalize">{key}:</label>
                          <p className="text-gray-900 mt-1">{value}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Validaciones */}
                  <div className="mt-6">
                    <h5 className="font-semibold text-gray-800 mb-3">Validaciones</h5>
                    {documents[selectedDocument]?.validationResults && (
                      <div className="space-y-2">
                        {Object.entries(documents[selectedDocument].validationResults).map(([key, valid]) => (
                          <div key={key} className="flex items-center space-x-2">
                            {valid ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                            <span className="text-sm capitalize">{key}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Impacto comercial */}
                  {documents[selectedDocument]?.businessImpact && (
                    <div className="mt-6 p-3 bg-green-50 rounded-lg">
                      <h5 className="font-semibold text-green-900 mb-2">💰 Impacto Comercial</h5>
                      <div className="text-xs text-green-800 space-y-1">
                        <div>Tiempo manual: {documents[selectedDocument].businessImpact.manualTime}</div>
                        <div>Tiempo ahorrado: {documents[selectedDocument].businessImpact.timeSaved}</div>
                        <div>Ahorro en costos: {documents[selectedDocument].businessImpact.costSaving}</div>
                        <div>Mejora en precisión: {documents[selectedDocument].businessImpact.accuracyImprovement}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer del modal */}
              <div className="bg-gray-50 px-6 py-4 border-t">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    ⚡ <strong>Eficiencia:</strong> Procesado en {documents[selectedDocument]?.processingTime} vs 15-30 min manual
                  </div>
                  <button
                    onClick={() => setShowDocumentDetail(false)}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de detalle de procesamiento */}
        {showProcessingDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
              {/* Header del modal */}
              <div className="bg-blue-600 text-white p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-semibold">Proceso de IA en Tiempo Real</h3>
                    <p className="text-blue-100">Pipeline de automatización paso a paso</p>
                  </div>
                  <button
                    onClick={() => setShowProcessingDetail(false)}
                    className="text-white hover:bg-blue-700 rounded-lg p-2"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6">
                <h4 className="font-semibold mb-6">Pasos del Procesamiento Inteligente</h4>
                
                {/* Steps del procesamiento */}
                {documents[selectedDocument]?.ocrSteps && (
                  <div className="space-y-4">
                    {documents[selectedDocument].ocrSteps.map((step, idx) => (
                      <div key={idx} className="flex items-center p-4 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <h5 className="font-medium text-gray-900">{step.step}</h5>
                          <p className="text-sm text-gray-600">
                            Completado en {step.time} • Confianza: {(step.confidence * 100).toFixed(1)}%
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">{(step.confidence * 100).toFixed(1)}%</div>
                          <div className="text-xs text-gray-500">{step.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Beneficios comerciales */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h5 className="font-semibold text-green-900 mb-2">💰 Ahorro vs Manual</h5>
                    <div className="text-sm text-green-800">
                      <div>• Tiempo: 16.6 min ahorrados por documento</div>
                      <div>• Costo: $8.50 ahorrados por procesamiento</div>
                      <div>• Precisión: +15% vs captura manual</div>
                      <div>• Disponibilidad: 24/7 sin descansos</div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-semibold text-blue-900 mb-2">🚀 Escalabilidad</h5>
                    <div className="text-sm text-blue-800">
                      <div>• Capacidad: Ilimitada</div>
                      <div>• Velocidad: Consistente siempre</div>
                      <div>• Calidad: No se degrada con volumen</div>
                      <div>• Crecimiento: Automático según demanda</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer del modal */}
              <div className="bg-gray-50 px-6 py-4 border-t">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    🎯 <strong>Resultado:</strong> Su equipo se enfoca en decisiones estratégicas, no en captura manual
                  </div>
                  <button
                    onClick={() => setShowProcessingDetail(false)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 